import { DocumentNode, gql } from "@apollo/client";

/**
 * GraphQL operations for the anonymous order-lookup (Public_Entry_Order_Lookup)
 * GIS funnel.
 *
 * The funnel matches the patient and resolves their GIS-eligible orders in a
 * single NonAuth call, then binds the per-order signed guest token to the
 * order the patient schedules. All subsequent GIS work is authorized by that
 * token via the NonAuth GIS/Acuity resolvers.
 */
class PublicGisOrderLookupService {
  /**
   * GIS NonAuth patient + order match. Matches the patient by demographics and
   * returns every GIS-eligible order, each with its own order-bound guest
   * `token`, plus a `code` describing a terminal outcome (no patient/order
   * match). Unlike the shared `matchPatientNonAuth` (used by the draw-request
   * flow, which errors on multiple orders), this never fails on multiple
   * matches: a single eligible order auto-routes while multiple orders surface
   * the order picker.
   */
  public static matchGisOrdersNonAuth = (): DocumentNode => gql`
    query matchGisOrdersNonAuth(
      $patientNonAuthInfoData: PatientNonAuthInfoData!
      $recaptchaToken: String
      $recaptchaV2Token: String
    ) {
      matchGisOrdersNonAuth(
        patientNonAuthInfoData: $patientNonAuthInfoData
        recaptchaToken: $recaptchaToken
        recaptchaV2Token: $recaptchaV2Token
      ) {
        code
        token
        orders {
          orderUidPublic
          testUid
          testType
          status
          counselingIntent
          resultCode
          createdAt
          limsCaseId
          token
        }
      }
    }
  `;

  /**
   * Creates the guest GIS triage session for the matched order, stamping
   * `scheduling_source = Public_Entry_Order_Lookup` and the order's pre/post
   * `result_status`. Authorized by the signed guest token.
   *
   * Deliberately without the confirmed contact arguments the mutation accepts:
   * what the visitor typed to find their order is a lookup answer, not contact
   * details they confirmed, and it travels as prefill instead. The intake's
   * confirm step is what records confirmed contact.
   */
  public static createOncUppGisTriageNonAuth = (): DocumentNode => gql`
    mutation createOncUppGisTriageNonAuth(
      $token: String!
      $orderUidPublic: String!
      $testUid: String!
      $schedulingSource: String
      $prefilledInput: UpdatePrefilledDataInput
    ) {
      createOncUppGisTriageNonAuth(
        token: $token
        orderUidPublic: $orderUidPublic
        testUid: $testUid
        schedulingSource: $schedulingSource
        prefilledInput: $prefilledInput
      ) {
        id
        orderUidPublic
        testUid
        schedulingSource
        resultStatus
      }
    }
  `;

  /**
   * Guest prefill for the matched order (token-authorized). Powers the ONC
   * intake confirm-info step.
   */
  public static getGisIntakeFormDataNonAuth = (): DocumentNode => gql`
    query getGisIntakeFormDataNonAuth(
      $token: String!
      $orderUidPublic: String!
      $testUid: String!
    ) {
      getGisIntakeFormDataNonAuth(
        token: $token
        orderUidPublic: $orderUidPublic
        testUid: $testUid
      ) {
        firstName
        lastName
        dateOfBirth
        phone
        email
        physicianClinic
        orderingPhysician
        cancerType
        cancerStage
        testType
        testOrderStatus
        hasGISAppointment
        hasPreviouslyTakenTest
      }
    }
  `;
}

export default PublicGisOrderLookupService;
